import type { ImageBypassRule } from '@vs-shop/headless/useVsImage';

/**
 * The image origins a deployment's own env names, in the shape
 * `@vs-shop/engine/image/imageOrigins` derives them. Declared structurally, so
 * this package needs no engine dependency.
 */
export type ImageBypassOrigins = {
  /** Host serving pre-generated Liip variants; `null` when no CDN is configured. */
  readonly cdnHostname: string | null;
  /** Host serving the on-demand `resolve` fallback; `null` when unconfigured. */
  readonly backendHostname: string | null;
};

export type BuildImageBypassRulesOptions = {
  readonly origins: ImageBypassOrigins;
  /** The backend path under which it generates a missing variant on demand. */
  readonly backendResolvePathPrefix: string;
  /**
   * The tenant's hosts that no env names: local development, office servers,
   * third parties, and a production floor for a build with no env.
   */
  readonly staticRules: readonly ImageBypassRule[];
};

/**
 * The optimizer-bypass rules one storefront renders with: the rules its
 * env-derived origins imply, then the tenant's static rules.
 *
 * - The CDN host bypasses whole. It serves pre-generated variants only.
 * - The backend host bypasses under the resolve prefix only. That subtree is
 *   the only part of the API entrypoint that serves images.
 *
 * An unconfigured origin contributes no rule. A host that reaches neither these
 * rules nor `images.remotePatterns` fails silently: `/_next/image` answers 400
 * and the fallback flow paints the missing-image placeholder.
 */
export function buildImageBypassRules({
  origins,
  backendResolvePathPrefix,
  staticRules,
}: BuildImageBypassRulesOptions): ImageBypassRule[] {
  return [
    ...(origins.cdnHostname === null ? [] : [{ host: origins.cdnHostname }]),
    ...(origins.backendHostname === null
      ? []
      : [{ host: origins.backendHostname, pathPrefix: backendResolvePathPrefix }]),
    ...staticRules,
  ];
}
